import { useState } from "react";
import { People } from "@mui/icons-material";
import { Button, Grid, Paper, useTheme, Typography, Chip, LinearProgress } from "@mui/material";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { Api } from "../../../../api/Api";
import {
  thunkCreateErrorNotification,
  thunkCreateSuccessNotification,
} from "../../../../store/actions/NotificationActions";
import ImsPaperHead from "../../../atoms/ImsPaperHead";
import { useMountEffect } from "../../../atoms/Utils";
import { AISegmentGroup } from "../../../../store/models/segments/AISegmentGroup";

interface Step1Props {
  onNext: (values: AISegmentGroup) => void;
  onValidationChange: (isValid: boolean) => void;
  initialValues?: AISegmentGroup;
  onBack: () => void;
}

const Step1 = ({
  onNext,
  onValidationChange,
  initialValues,
  onBack,
  thunkCreateErrorNotification,
}: Step1Props & ConnectedProps<typeof connector>) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [customerRetentionGroup, setCustomerRetentionGroup] = useState<AISegmentGroup>({} as AISegmentGroup);
  const [customerLifetimeValueGroup, setCustomerLifetimeValueGroup] = useState<AISegmentGroup>({} as AISegmentGroup);
  const [selectedModel, setSelectedModel] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(true);

  useMountEffect(() => {
    // Fetch models from API
    const getCustomerRetention = async () => {
      try {
        const response = await Api.getCustomerRetentionGroupDetail();
        response.data.modelType = "ci";
        setCustomerRetentionGroup(response.data);
      } catch (error) {
        thunkCreateErrorNotification(t("segmentBuilder.messages.errorLoadingModel"), error);
      }
    };

    const getCustomerLifetimeValue = async () => {
      try {
        const response = await Api.getCustomerLifetimeValueGroupDetail();
        response.data.modelType = "clv";
        setCustomerLifetimeValueGroup(response.data);
      } catch (error) {
        thunkCreateErrorNotification(t("segmentBuilder.messages.errorLoadingModel"), error);
      }
    };

    getCustomerRetention();
    getCustomerLifetimeValue();
    setIsLoading(false);
  });

  // Handle card selection and navigation in one step
  const handleCardSelect = (model: AISegmentGroup) => {
    if (model?.id) {
      setSelectedModel(model);
      onNext(model); // Immediately proceed to next step
    }
  };

  return (
    <Form
      onSubmit={(_) => {
        // This is now less important since we're handling navigation directly in card click
        if (selectedModel) {
          onNext({ ...selectedModel });
        }
      }}
      initialValues={initialValues}
      render={({ handleSubmit, valid, values }) => {
        // Update parent's validation state
        onValidationChange(valid);

        return (
          <form onSubmit={handleSubmit}>
            {isLoading && <LinearProgress />}
            <Paper style={{ padding: theme.spacing(3), marginTop: theme.spacing(6) }}>
              <ImsPaperHead text={t("segmentBuilder.ai.step1.title")} />
              <Grid container spacing={2}>
                <Grid item sm={12}>
                  <h3>{t("segmentBuilder.ai.step1.description")}</h3>
                  <Grid container spacing={2}>
                    {/* customerRetentionGroup */}
                    <Grid item sm={6} key="customerRetentionGroup">
                      <Paper
                        onClick={() => customerRetentionGroup.id && handleCardSelect(customerRetentionGroup)}
                        data-testid="customerRetentionGroupPaper"
                        elevation={selectedModel === customerRetentionGroup ? 2 : 1}
                        sx={{
                          padding: theme.spacing(2),
                          backgroundColor: selectedModel === customerRetentionGroup ? "#EAEAEA" : "#FFFFFF",
                          cursor: customerRetentionGroup.id ? "pointer" : "not-allowed",
                          display: "flex",
                          height: "100%",
                          border: selectedModel === customerRetentionGroup ? `1px solid gray` : "none",
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            backgroundColor: customerRetentionGroup.id ? "#E0E0E0" : undefined,
                            elevation: customerRetentionGroup.id ? 2 : undefined,
                            boxShadow: customerRetentionGroup.id ? theme.shadows[2] : undefined,
                          },
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingRight: theme.spacing(2),
                            borderRight: `1px solid gray`,
                            marginRight: theme.spacing(2),
                          }}
                        >
                          <People />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Typography variant="h6">
                            {t("segmentBuilder.ai.groups.customerRetention.name")}
                            <CustomerRetentionStatusLabel
                              status={customerRetentionGroup.status}
                              style={{ marginLeft: "4px", marginBottom: "2px" }}
                            />
                          </Typography>
                          <Typography variant="body2">
                            {t("segmentBuilder.ai.groups.customerRetention.description")}
                          </Typography>
                        </div>
                      </Paper>
                    </Grid>

                    {/* customerLifetimeValueGroup */}
                    <Grid item sm={6} key="customerLifetimeValueGroup">
                      <Paper
                        onClick={() => customerLifetimeValueGroup.id && handleCardSelect(customerLifetimeValueGroup)}
                        elevation={1}
                        data-testid="customerLifetimeValueGroup"
                        sx={{
                          padding: theme.spacing(2),
                          backgroundColor: selectedModel === customerLifetimeValueGroup ? "#EAEAEA" : "#FFFFFF",
                          cursor: customerLifetimeValueGroup.id ? "pointer" : "not-allowed",
                          display: "flex",
                          height: "100%",
                          border: selectedModel === customerLifetimeValueGroup ? `1px solid gray` : "none",
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            backgroundColor: customerLifetimeValueGroup.id ? "#E0E0E0" : undefined,
                            elevation: customerLifetimeValueGroup.id ? 2 : undefined,
                            boxShadow: customerLifetimeValueGroup.id ? theme.shadows[2] : undefined,
                          },
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingRight: theme.spacing(2),
                            borderRight: `1px solid gray`,
                            marginRight: theme.spacing(2),
                          }}
                        >
                          <People />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Typography variant="h6">
                            {t("segmentBuilder.ai.groups.customerLifetimeValue.name")}{" "}
                            <CustomerLifetimeValueStatusLabel
                              status={customerLifetimeValueGroup.status}
                              style={{ marginLeft: "4px", marginBottom: "2px" }}
                            />
                          </Typography>
                          <Typography variant="body2">
                            {t("segmentBuilder.ai.groups.customerLifetimeValue.description")}
                          </Typography>
                        </div>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: theme.spacing(2) }}>
              <Button color="secondary" variant="contained" style={{ minWidth: 150 }} onClick={onBack}>
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

const CustomerRetentionStatusLabel: React.FC<{ status: AISegmentGroup["status"]; style?: React.CSSProperties }> = ({
  status,
  style,
}) => {
  const { t } = useTranslation();

  switch (status) {
    case "enabled":
      return (
        <Chip label={t("segmentBuilder.ai.groups.customerRetention.status.enabled")} color="success" style={style} />
      );
    case "disabled":
      return (
        <Chip label={t("segmentBuilder.ai.groups.customerRetention.status.disabled")} color="error" style={style} />
      );
    default:
      return (
        <Chip label={t("segmentBuilder.ai.groups.customerRetention.status.unknown")} color="warning" style={style} />
      );
  }
};

const CustomerLifetimeValueStatusLabel: React.FC<{ status: AISegmentGroup["status"]; style?: React.CSSProperties }> = ({
  status,
  style,
}) => {
  const { t } = useTranslation();

  switch (status) {
    case "enabled":
      return (
        <Chip label={t("segmentBuilder.ai.groups.customerLifetimeValue.status.enabled")} color="success" style={style} />
      );
    case "disabled":
      return (
        <Chip label={t("segmentBuilder.ai.groups.customerLifetimeValue.status.disabled")} color="error" style={style} />
      );
    default:
      return (
        <Chip label={t("segmentBuilder.ai.groups.customerLifetimeValue.status.unknown")} color="warning" style={style} />
      );
  }
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      thunkCreateSuccessNotification,
      thunkCreateErrorNotification,
    },
    dispatch
  );

const connector = connect(null, mapDispatchToProps);

export default connector(Step1);
